// src/helpers/fetchMetaspaces.ts

import { ApiClient } from '@center-technologies/shared-api-clients';
import { MetaspaceBasicOption, Metaspace } from 'types/metaspace';

export interface FetchMetaspacesParams {
  systemNames?: string[];
}

interface FetchMetaspacesBasicResponse {
  metaspaces: MetaspaceBasicOption[];
}

interface FetchMetaspacesFullResponse {
  metaspaces: Metaspace[];
}

/**
 * Fetch metaspaces (basic or full).
 */
async function fetchMetaspacesGeneric<T>(
  apiClient: ApiClient,
  basic: boolean,
  params: FetchMetaspacesParams
): Promise<T[]> {
  const { systemNames } = params;

  // Build the query params object
  const axiosParams: Record<string, any> = { basic };
  if (systemNames?.length) {
    axiosParams['system_names'] = systemNames;
  }

  try {
    // GET /metaspaces?basic=true&system_names=foo&system_names=bar
    const response = await apiClient.get<{ metaspaces: T[] }>(
      '/metaspaces',
      { params: axiosParams }
    );
    return response.metaspaces;
  } catch (err: any) {
    console.error('[fetchMetaspaces] error', err.response?.data ?? err);
    // if 404, return empty list
    if (err.response?.status === 404) {
      return [];
    }
    throw new Error(
      err.response?.data?.error ??
      err.message ??
      'Failed to fetch metaspaces.'
    );
  }
}

/**
 * Fetches metaspaces data with basic details.
 */
export async function fetchMetaspacesBasicData(
  apiClient: ApiClient,
  params: FetchMetaspacesParams = {}
): Promise<FetchMetaspacesBasicResponse> {
  const metaspaces = await fetchMetaspacesGeneric<MetaspaceBasicOption>(
    apiClient,
    true,
    params
  );
  return { metaspaces };
}

/**
 * Fetches full metaspaces data with detailed information.
 */
export async function fetchMetaspacesFullData(
  apiClient: ApiClient,
  params: FetchMetaspacesParams = {}
): Promise<FetchMetaspacesFullResponse> {
  const metaspaces = await fetchMetaspacesGeneric<Metaspace>(
    apiClient,
    false,
    params
  );
  return { metaspaces };
}
